import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { TR } from "./translations";
import { useLanguage } from "../../context/LanguageContext";

// Idioma de la landing — réplica de la referencia:
//   · localStorage('ulture_lang'), default 'es' (línea 1130 de la referencia)
//   · dir="rtl" + lang cuando lang === 'ar'
// Se sincroniza además con la clave del resto del sitio ("ulture-language",
// LanguageContext legado) cuando el código existe en ambos sistemas.
export type LandingLang = "es" | "en" | "it" | "ar" | "zh" | "nl" | "fr";

interface Ctx {
  lang: LandingLang;
  setLang: (l: LandingLang) => void;
  t: any;
  dir: "ltr" | "rtl";
}

const LandingLanguageContext = createContext<Ctx | null>(null);

// La landing tiene 7 idiomas y el contexto legado (blog/booking/legales) cubre
// es/en/it/fr/nl/pt/ar/zh, así que todos pasan tal cual (cualquier otro → inglés).
const toLegacy = (code: LandingLang): "es" | "en" | "it" | "fr" | "nl" | "ar" | "zh" =>
  (["es", "en", "it", "fr", "nl", "ar", "zh"] as const).includes(code as any) ? (code as any) : "en";

// Red de seguridad por clave: el idioma elegido se fusiona sobre el español
// (idioma fuente), así una clave que falte se ve en español y nunca `undefined`.
// Los arrays se sustituyen enteros. TR no se muta, así que basta con calcularlo
// una vez por idioma.
type PlainObject = Record<string, unknown>;
const isPlainObject = (v: unknown): v is PlainObject =>
  v !== null && typeof v === "object" && !Array.isArray(v);
function mergeOver(base: unknown, over: unknown): unknown {
  if (over === undefined || over === null) return base;
  if (!isPlainObject(base) || !isPlainObject(over)) return over;
  const out: PlainObject = { ...base };
  for (const k of Object.keys(over)) out[k] = mergeOver(base[k], over[k]);
  return out;
}
const mergedTR: Record<string, unknown> = {};
function trFor(code: string): unknown {
  if (code === "es" || !TR[code]) return TR.es;
  if (!mergedTR[code]) mergedTR[code] = mergeOver(TR.es, TR[code]);
  return mergedTR[code];
}

export function LandingLanguageProvider({ children }: { children: React.ReactNode }) {
  const legacy = useLanguage(); // contexto legado (blog, booking, legales)
  const [lang, setLangState] = useState<LandingLang>(() => {
    try {
      const v = localStorage.getItem("ulture_lang");
      if (v && TR[v]) return v as LandingLang;
    } catch (e) { /* SSR/prerender */ }
    return "es";
  });

  const setLang = (code: LandingLang) => {
    try { localStorage.setItem("ulture_lang", code); } catch (e) { /* noop */ }
    setLangState(code);
  };

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  // Sincroniza el idioma VISIBLE de la landing con el estado EN VIVO del contexto
  // legado (no solo con localStorage). Antes el legado leía su idioma una única
  // vez al montar la app y la landing solo escribía localStorage, así que el blog
  // se quedaba con un idioma viejo (p. ej. "ar" de una sesión anterior) sin
  // relación con el español que se veía. Con esto, al entrar al blog/booking el
  // idioma siempre coincide con la landing. Se ejecuta también en el montaje.
  useEffect(() => {
    legacy.setLanguage(toLegacy(lang) as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const value = useMemo<Ctx>(
    () => ({ lang, setLang, t: trFor(lang), dir: lang === "ar" ? "rtl" : "ltr" }),
    [lang]
  );

  return <LandingLanguageContext.Provider value={value}>{children}</LandingLanguageContext.Provider>;
}

export function useLandingLanguage() {
  const ctx = useContext(LandingLanguageContext);
  if (!ctx) throw new Error("useLandingLanguage must be used within LandingLanguageProvider");
  return ctx;
}
